import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { historyBack } from '../../utils'
import { NotificationService } from '../../services'
import { Payment, PaymentType } from 'depoto-core/src/entities'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { PaymentService } from '../../services/entity-services/payment.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

const paymentSchema = {
  id: null,
  name: null,
  type: {
    id: null,
    name: null,
  },
  dispatchUnpaid: null,
}

const paymentTypeSchema = {
  id: null,
  name: null,
}

@Component({
  selector: 'app-payment',
  templateUrl: 'payment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentComponent implements CheckDataChanges, OnDestroy {
  private paymentService = inject(PaymentService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)
  submitted = signal(false)
  paymentTypes: PaymentType[] = []
  dispatchUnpaidOptions = ['yes', 'no']

  paymentForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    type: new FormControl({ value: '', disabled: false }, []),
    dispatchUnpaid: new FormControl({ value: 'no', disabled: false }, []),
  })

  readonly queryPaymentTypes = injectQuery(() => ({
    queryKey: ['paymentTypes', 'options'],
    queryFn: async () => {
      return await this.paymentService.getPaymentTypes({}, paymentTypeSchema).then(result => result.items)
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.payment-id')
    },
  }))

  readonly queryPayment = injectQuery(() => ({
    queryKey: ['payment', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.paymentService.getPayment(id, paymentSchema)
      } else {
        return new Payment()
      }
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.payment-id')
      this.fillFormValue(data)
    },
  }))

  createPayment = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.paymentForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return await this.paymentService.createPayment(n, paymentSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.paymentForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.paymentForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('ALERT PAYMENT CREATED'))

      // Update detail cache for this payment
      this.queryClient.setQueryData(['payment', Number(data['id'])], data)

      // OPTIONS: update options/list caches so selects/filters see the new payment immediately
      updateCachedListQueries(this.queryClient, ['payments', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['payments', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removePaymentListings()

      this.router.navigate(['/payment/', data['id']], { replaceUrl: true })
    },
  }))

  updatePayment = injectMutation(() => ({
    mutationFn: () => {
      if (this.paymentForm.invalid) {
        throw new Error('Form is invalid')
      }
      const paymentDataToSave = this.getDataToSave()
      return this.paymentService.updatePayment(paymentDataToSave, paymentSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.paymentForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.paymentForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('ALERT PAYMENT UPDATED'))

      // Update detail cache for this payment
      this.queryClient.setQueryData(['payment', data['id']], data)

      // OPTIONS: update options cache value for this payment
      updateCachedListQueries(this.queryClient, ['payments', 'options'], data, { mode: 'update' })

      // LISTING ['payments', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removePaymentListings()

      this.router.navigate(['/payment', data['id']])
    },
  }))

  deletePayment = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      const confirmation = this.translateService.instant('payment.delete.confirmation', {
        name: this.paymentForm.value.name,
      })

      if (confirmation) {
        await this.paymentService.deletePayment(this.queryPayment.data()).then(() => {
          this.notificationService.success(this.translateService.instant('ALERT PAYMENT DELETED'))

          const idStr = String(this.router.url.split('/')[2])

          // OPTIONS: keep as-is → update options cache by removing the payment
          updateCachedListQueries(this.queryClient, ['payments', 'options'], { id: Number(idStr) } as any, {
            mode: 'delete',
          })

          // LISTING ['payments', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
          this.removeFromListingsIfPresent(Number(idStr))

          // Optionally refresh listing pages
          this.router.navigate(['/payments'], { replaceUrl: true })
        })
      }
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return (
      this.queryPayment.isFetching() ||
      this.queryPayment.isPending() ||
      this.queryPaymentTypes.isFetching() ||
      this.queryPaymentTypes.isPending()
    )
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different payment ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    effect(() => {
      const pT = this.queryPaymentTypes.data()
      const d = this.queryPayment.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.paymentForm.pristine) {
        this.fillFormValue(d)
      }
    })
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.paymentForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // If snapshot not ready yet, rely on reactive form state
    if (!initial) {
      return this.paymentForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    return (
      norm(current?.name) !== norm(initial?.name) ||
      norm(current?.type) !== norm(initial?.type) ||
      norm(current?.dispatchUnpaid ?? 'no') !== norm(initial?.dispatchUnpaid ?? 'no')
    )
  }

  goBack(): void {
    historyBack()
  }

  public fillFormValue(payment) {
    // const data = this.queryPayment.data() as any
    const value = {
      name: payment?.name ?? '',
      type: payment?.type?.id ?? '',
      dispatchUnpaid: payment?.dispatchUnpaid ? 'yes' : 'no',
    }
    this.paymentForm.setValue(value)
    // Snapshot initial values and mark pristine for unsaved-changes detection
    this.initialFormValue.set(structuredClone(value))
    this.paymentForm.markAsPristine()
  }

  getDataToSave() {
    const current = this.paymentForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    // type (by id) — include only when changed
    if (norm(current.type) !== norm(initial?.type)) {
      patch.type = current.type ? { id: current.type } : null
    }

    // dispatchUnpaid (stored as 'yes'/'no' in form) — include only when changed
    const currDispatch = current.dispatchUnpaid ?? 'no'
    const initDispatch = initial?.dispatchUnpaid ?? 'no'
    if (String(currDispatch) !== String(initDispatch)) {
      patch.dispatchUnpaid = currDispatch === 'yes'
    }

    return patch
  }

  // ===== Helpers for cache handling of payment listings =====
  private isPaymentsListingKey(key: unknown[]): boolean {
    // We already filtered with getQueriesData({ queryKey: ['payments'] }),
    // so only ensure the key is an array that starts with 'payments'.
    return Array.isArray(key) && key[0] === 'payments'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached) return false

    // Infinite query shape: { pages: [...] }
    if (Array.isArray(cached.pages)) {
      for (const page of cached.pages) {
        const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
        if (!Array.isArray(items)) continue
        if (items.some((it: any) => String(it?.id) === idStr)) return true
      }
      return false
    }

    // Plain array shape
    if (Array.isArray(cached)) {
      return cached.some((it: any) => String(it?.id) === idStr)
    }

    // Object with items array
    if (Array.isArray(cached?.items)) {
      return cached.items.some((it: any) => String(it?.id) === idStr)
    }

    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached) return
    const idStr = String(id)

    // Infinite query shape: { pages: [...] }
    if (Array.isArray(cached.pages)) {
      const newPages = cached.pages.map((page: any) => {
        if (Array.isArray(page)) {
          return page.filter((it: any) => String(it?.id) !== idStr)
        }
        if (Array.isArray(page?.items)) {
          return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
        }
        return page
      })
      this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
      return
    }

    // Plain array shape
    if (Array.isArray(cached)) {
      this.queryClient.setQueryData(
        key as any,
        cached.filter((it: any) => String(it?.id) !== idStr)
      )
      return
    }

    // Object with items array
    if (Array.isArray(cached?.items)) {
      this.queryClient.setQueryData(key as any, {
        ...cached,
        items: cached.items.filter((it: any) => String(it?.id) !== idStr),
      })
      return
    }
  }

  // CREATE → refetch all cached payment listings (['payments', sortProp, sortDir])
  private removePaymentListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['payments'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['payments'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isPaymentsListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.paymentForm.controls
  }
}
